/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {
	ActionTypes,
	flexServiceWorkerRegistrationSceneActivated,
	flexServiceWorkerRegistrationSceneEnterPasswordUnexpectedError,
	flexServiceWorkerRegistrationSceneEnterPasswordValidationException,
	flexServiceWorkerRegistrationSceneEnterPhoneNumberUnexpectedError,
	flexServiceWorkerRegistrationSceneEnterPhoneNumberValidationException,
	flexServiceWorkerRegistrationSceneEnterPhoneVerificationCodeUnexpectedError,
	flexServiceWorkerRegistrationSceneEnterPhoneVerificationCodeValidationException,
	flexServiceWorkerRegistrationScenePasswordInputChanged,
	flexServiceWorkerRegistrationScenePasswordStrengthEvaluated,
	flexServiceWorkerRegistrationScenePasswordSubmitted,
	flexServiceWorkerRegistrationScenePhoneNumberLookupInputChanged,
	flexServiceWorkerRegistrationScenePhoneNumberLookupResolved,
	flexServiceWorkerRegistrationScenePhoneNumberLookupSubmitted,
	flexServiceWorkerRegistrationScenePhoneVerificationCodeInputChanged,
	flexServiceWorkerRegistrationScenePhoneVerificationCodeSubmitted,
	flexServiceWorkerRegistrationSceneRequestResolvePersonalName,
	flexServiceWorkerRegistrationSceneRequestStopPhoneVerification,
	flexServiceWorkerRegistrationSceneResolvePersonalNameUnexpectedError,
	flexServiceWorkerRegistrationSceneStateChanged,
	flexServiceWorkerRegistrationSceneStateRetrieved,
	flexServiceWorkerRegistrationSceneStopPhoneVerificationUnexpectedError
} from './actions';
import {getType} from 'typesafe-actions';
import {combineReducers} from 'redux';
import {MessageType} from '../../model/messageType';
import {DisplayType} from './displayType';
import {ConstraintViolationDTO} from '../../model/validation';
import {Country} from '../../model/country';
import {FlexServiceWorkerRegistrationStateDTO, PasswordStrengthDTO} from '../../generated/iam-client';

export interface EnterPasswordState {
	email: string;
	minimumStrength: number;
	strength: number;
	submitDisabled: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const enterPasswordInitialState: EnterPasswordState = {
	email: '',
	minimumStrength: 0,
	strength: 0,
	submitDisabled: false,
	violations: {}
};

export interface IndexState {
	displayType: DisplayType;
	message: string;
	messageType: MessageType;
}

const indexInitialState: IndexState = {
	displayType: DisplayType.DISPLAY_TYPE_NONE,
	message: '',
	messageType: MessageType.INFO
};

export interface PersonalNameState {
	submitDisabled: boolean;
}

const personalNameInitialState: PersonalNameState = {
	submitDisabled: false
};

export interface PhoneNumberState {
	lookupValue: {
		country: Country;
		phoneNumber: string;
	};
	maximumDurationInSeconds: number | null;
	phoneNumberLookupActive: boolean;
	phoneNumberLookupResolved: boolean;
	phoneNumberLookupSubmitDisabled: boolean;
	verificationCode: string;
	verificationCodeActive: boolean;
	verificationCodeSubmitDisabled: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const phoneNumberInitialState: PhoneNumberState = {
	lookupValue: {
		country: 'NETHERLANDS',
		phoneNumber: ''
	},
	maximumDurationInSeconds: null,
	phoneNumberLookupActive: false,
	phoneNumberLookupResolved: false,
	phoneNumberLookupSubmitDisabled: false,
	verificationCode: '',
	verificationCodeActive: false,
	verificationCodeSubmitDisabled: false,
	violations: {}
};

export interface FlexServiceWorkerRegistrationState {
	enterPassword: EnterPasswordState;
	index: IndexState;
	personalName: PersonalNameState;
	phoneNumber: PhoneNumberState;
}

const enterPasswordReducer = (
	state: EnterPasswordState = enterPasswordInitialState,
	action: ActionTypes
): EnterPasswordState => {

	let requestedPasswordMinimumStrength: PasswordStrengthDTO | undefined;
	let insufficientEvaluatedPasswordStrength: PasswordStrengthDTO | undefined;
	const violations = {...state.violations};
	let strength = state.strength;
	let submitDisabled = state.submitDisabled;
	switch (action.type) {
		case getType(flexServiceWorkerRegistrationSceneActivated):
			return enterPasswordInitialState;
		case getType(flexServiceWorkerRegistrationSceneStateRetrieved):
		case getType(flexServiceWorkerRegistrationSceneStateChanged):
			requestedPasswordMinimumStrength = action.payload.state.requestedPasswordMinimumStrength;
			insufficientEvaluatedPasswordStrength = action.payload.state.insufficientEvaluatedPasswordStrength;

			if (action.payload.state.passwordNotChangedWithSamePassword) {
				violations.password = [{message: 'Het nieuwe wachtwoord mag niet gelijk zijn aan het huidige wachtwoord'}];
				submitDisabled = false;
			}

			if (insufficientEvaluatedPasswordStrength) {
				violations.password = [{message: 'Het nieuwe wachtwoord is niet sterk (veilig) genoeg'}];
				strength = insufficientEvaluatedPasswordStrength.strength;
				submitDisabled = false;
			}

			return {
				...state,
				email: action.payload.state.accountName,
				minimumStrength: requestedPasswordMinimumStrength ? requestedPasswordMinimumStrength.strength : 0,
				strength,
				submitDisabled,
				violations

			};
		case getType(flexServiceWorkerRegistrationScenePasswordInputChanged):
			return {
				...state,
				violations: {}
			};
		case getType(flexServiceWorkerRegistrationScenePasswordStrengthEvaluated):
			return {
				...state,
				strength: action.payload.passwordStrength.strength
			};
		case getType(flexServiceWorkerRegistrationScenePasswordSubmitted):
			return {
				...state,
				submitDisabled: true
			};
		case getType(flexServiceWorkerRegistrationSceneEnterPasswordValidationException):
			return {
				...state,
				submitDisabled: false,
				violations: action.payload.violations
			};
		case getType(flexServiceWorkerRegistrationSceneEnterPasswordUnexpectedError):
			return {
				...state,
				submitDisabled: false
			};
		default:
			return state;
	}
};

const indexReducer = (
	state: IndexState = indexInitialState,
	action: ActionTypes
): IndexState => {
	switch (action.type) {
		case getType(flexServiceWorkerRegistrationSceneActivated):
			return indexInitialState;
		case getType(flexServiceWorkerRegistrationSceneStateRetrieved):
		case getType(flexServiceWorkerRegistrationSceneStateChanged):
			if (action.payload.state.phoneNumberRequested || action.payload.state.verificationCodeRequested)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_PHONE_NUMBER
				};

			if (action.payload.state.personalNameRequested) {
				let message = 'Bij het omzetten van uw account kunnen wij niet alle benodigde gegevens verifieren.' +
					' Neem contact met ons op, zodat wij uw gegevens handmatig kunnen verifieren. Hierna kunt u het opnieuw proberen.';
				if (action.payload.state.personalNameFailed)
					message = 'Wij hebben geen antwoord gekregen van het systeem.' +
						' Wij kunnen uw gegevens nu niet omzetten. U kunt het opnieuw proberen.';

				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_PERSONAL_NAME,
					message,
					messageType: MessageType.ERROR
				};
			}

			if (action.payload.state.requestedPasswordMinimumStrength)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_ENTER_PASSWORD,
					message: 'Kies een nieuw wachtwoord',
					messageType: MessageType.INFO
				};

			return state;
		default:
			return state;
	}
};

const personalNameReducer = (
	state: PersonalNameState = personalNameInitialState,
	action: ActionTypes
): PersonalNameState => {
	switch (action.type) {
		case getType(flexServiceWorkerRegistrationSceneActivated):
			return personalNameInitialState;
		case getType(flexServiceWorkerRegistrationSceneStateRetrieved):
		case getType(flexServiceWorkerRegistrationSceneStateChanged):
			return {
				...state,
				submitDisabled: !action.payload.state.personalNameRequested
			};
		case getType(flexServiceWorkerRegistrationSceneRequestResolvePersonalName):
			return {
				...state,
				submitDisabled: true
			};

		case getType(flexServiceWorkerRegistrationSceneResolvePersonalNameUnexpectedError):
			return {
				...state,
				submitDisabled: false
			};
		default:
			return state;
	}
};

const phoneNumberReducer = (
	state: PhoneNumberState = phoneNumberInitialState,
	action: ActionTypes
): PhoneNumberState => {
	let phoneNumberRequested: boolean;
	let verificationCodeRequested: boolean;
	let phoneVerificationTimeRemainingInSeconds: number | undefined;
	let phoneVerificationStoppedWithReason: FlexServiceWorkerRegistrationStateDTO['phoneVerificationStoppedWithReason'];
	let phoneNumberLookupViolationMessage = '';
	let phoneNumberLookupViolations: ConstraintViolationDTO[];
	let verificationCodeViolationMessage = '';
	let verificationCodeViolations: ConstraintViolationDTO[];

	switch (action.type) {
		case getType(flexServiceWorkerRegistrationSceneActivated):
			return phoneNumberInitialState;
		case getType(flexServiceWorkerRegistrationSceneStateRetrieved):
		case getType(flexServiceWorkerRegistrationSceneStateChanged):
			phoneNumberRequested = action.payload.state.phoneNumberRequested;
			verificationCodeRequested = action.payload.state.verificationCodeRequested;
			phoneVerificationTimeRemainingInSeconds = action.payload.state.phoneVerificationTimeRemainingInSeconds;
			phoneVerificationStoppedWithReason = action.payload.state.phoneVerificationStoppedWithReason;
			if (action.payload.state.phoneNumberFailed)
				phoneNumberLookupViolationMessage = 'Wij kunnen op dit moment het nummer niet verifieren. Probeer het later nog eens';
			else if (action.payload.state.phoneNumberInvalid)
				phoneNumberLookupViolationMessage = 'Dit nummer is niet bij ons bekend, kies een ander nummer';
			else if (action.payload.state.phoneNumberDenied)
				phoneNumberLookupViolationMessage = 'Dit nummer is al in gebruik, kies een ander nummer';
			else if (action.payload.state.phoneNumberBlocked)
				phoneNumberLookupViolationMessage = 'Dit nummer is geblokeerd. Neem contact met ons op';

			phoneNumberLookupViolations =
				phoneNumberLookupViolationMessage !== '' ? [{message: phoneNumberLookupViolationMessage}] : [];

			if (action.payload.state.verificationCodeFailed && state.verificationCodeActive)
				verificationCodeViolationMessage = 'Wij kunnen op dit moment de code niet verifieren. Probeer het later nog eens';

			if (!!phoneVerificationStoppedWithReason && !action.payload.state.phoneNumberUnblocked && state.verificationCodeActive)
				switch (phoneVerificationStoppedWithReason) {
					case 'INVALID_VERIFICATION_CODE':
						verificationCodeViolationMessage = 'De ingevoerde verificatiecode was niet geldig';
						break;
					case 'VERIFICATION_TIMEOUT':
						verificationCodeViolationMessage = 'Uw verificatiecode is verlopen';
						break;
					default:
						verificationCodeViolationMessage = '';
				}

			verificationCodeViolations = verificationCodeViolationMessage !== '' ? [{message: verificationCodeViolationMessage}] : [];
			return {
				...state,
				maximumDurationInSeconds: (
					phoneVerificationTimeRemainingInSeconds &&
					Number.isInteger(phoneVerificationTimeRemainingInSeconds) &&
					phoneVerificationTimeRemainingInSeconds > 0
				) ? phoneVerificationTimeRemainingInSeconds : null,
				phoneNumberLookupActive: phoneNumberRequested,
				phoneNumberLookupSubmitDisabled:
					!phoneNumberRequested ||
					(phoneNumberLookupViolations.length === 0 ?
						state.phoneNumberLookupSubmitDisabled && state.phoneNumberLookupActive : false),
				verificationCodeActive: verificationCodeRequested,
				verificationCodeSubmitDisabled:
					!verificationCodeRequested ||
					(verificationCodeViolations.length === 0 ?
						state.verificationCodeSubmitDisabled && state.verificationCodeActive : false),
				violations: {
					...state.violations,
					phoneNumberLookup: phoneNumberLookupViolations,
					verificationCode: verificationCodeViolations
				}
			};
		case getType(flexServiceWorkerRegistrationScenePhoneNumberLookupInputChanged):
			return {
				...state,
				lookupValue: {
					country: action.payload.country,
					phoneNumber: action.payload.phoneNumber
				},
				phoneNumberLookupResolved: false,
				violations: {}
			};
		case getType(flexServiceWorkerRegistrationScenePhoneNumberLookupResolved):
			return {
				...state,
				lookupValue: {
					country: action.payload.phoneNumber.country,
					phoneNumber: action.payload.phoneNumber.formattedNationalNumber
				},
				phoneNumberLookupResolved: true
			};
		case getType(flexServiceWorkerRegistrationScenePhoneNumberLookupSubmitted):
			return {
				...state,
				phoneNumberLookupSubmitDisabled: true,
				verificationCode: '',
				violations: {}
			};
		case getType(flexServiceWorkerRegistrationSceneEnterPhoneNumberValidationException):
			return {
				...state,
				phoneNumberLookupSubmitDisabled: false,
				violations: {...state.violations, phoneNumberLookup: [{message: 'Geef een geldig telefoonnumer op'}]}
			};
		case getType(flexServiceWorkerRegistrationSceneEnterPhoneNumberUnexpectedError):
			return {
				...state,
				phoneNumberLookupSubmitDisabled: false
			};
		case getType(flexServiceWorkerRegistrationScenePhoneVerificationCodeInputChanged):
			return {
				...state,
				verificationCode: action.payload.verificationCode,
				violations: {}
			};
		case getType(flexServiceWorkerRegistrationScenePhoneVerificationCodeSubmitted):
		case getType(flexServiceWorkerRegistrationSceneRequestStopPhoneVerification):
			return {
				...state,
				verificationCodeSubmitDisabled: true,
				violations: {}
			};
		case getType(flexServiceWorkerRegistrationSceneEnterPhoneVerificationCodeValidationException):
			return {
				...state,
				verificationCodeSubmitDisabled: false,
				violations: {...state.violations, verificationCode: [{message: 'Geef een geldige verificatie code op'}]}
			};
		case getType(flexServiceWorkerRegistrationSceneEnterPhoneVerificationCodeUnexpectedError):
		case getType(flexServiceWorkerRegistrationSceneStopPhoneVerificationUnexpectedError):
			return {
				...state,
				verificationCodeSubmitDisabled: false
			};
		default:
			return state;
	}
};

export default combineReducers({
	enterPassword: enterPasswordReducer,
	index: indexReducer,
	personalName: personalNameReducer,
	phoneNumber: phoneNumberReducer
});
